import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      },
    );

    this.#setupSplitScroll();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector("media-gallery");

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  // -------------------------
  // Split scroll logic
  // -------------------------
  #setupSplitScroll() {
    if (this.presentation !== "split_scroll") return;

    if (window.matchMedia("(max-width: 749px)").matches) return;
    if ("ontouchstart" in window || navigator.maxTouchPoints > 0) return;

    const scroller = this.refs.splitScroller;
    if (!scroller) return;

    window.addEventListener("wheel", this.#onGlobalWheel, {
      passive: false,
      capture: true,
      signal: this.#controller.signal,
    });

    window.addEventListener("mousewheel", this.#onGlobalWheel, {
      passive: false,
      capture: true,
      signal: this.#controller.signal,
    });
  }

  #getSplitContainer() {
    return this.querySelector(".media-gallery-split");
  }

  #isGalleryVisible() {
    const split = this.#getSplitContainer();
    if (!split) return false;

    const rect = split.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;

    return (
      rect.top < viewportHeight * 0.85 && rect.bottom > viewportHeight * 0.15
    );
  }

  #isGalleryAtTop() {
    const split = this.#getSplitContainer();
    if (!split) return false;

    const rect = split.getBoundingClientRect();

    const headerOffset =
      parseFloat(
        getComputedStyle(document.body).getPropertyValue(
          "--header-group-height",
        ),
      ) || 0;

    // Only treat it as "at top" when the split container is actually
    // aligned very close to the top under the header
    return Math.abs(rect.top - headerOffset) <= 6;
  }

  #onGlobalWheel = (e) => {
    if (this.presentation !== "split_scroll") return;

    const scroller = this.refs.splitScroller;
    if (!scroller) return;
    if (!this.#isGalleryVisible()) return;

    const maxScroll = scroller.scrollHeight - scroller.clientHeight;
    if (maxScroll <= 0) return;

    const delta =
      e.deltaY ??
      (e.wheelDeltaY ? -e.wheelDeltaY : e.wheelDelta ? -e.wheelDelta : 0);

    if (!delta) return;

    const atTop = scroller.scrollTop <= 1;
    const atBottom = scroller.scrollTop >= maxScroll - 1;

    const scrollingDown = delta > 0;
    const scrollingUp = delta < 0;

    let shouldConsume = false;

    if (scrollingDown) {
      // While going down, right side scrolls first until its end
      shouldConsume = !atBottom;
    } else if (scrollingUp) {
      // While coming up, page should move first.
      // Only when gallery is actually back at the top should right side scroll upward.
      shouldConsume = this.#isGalleryAtTop() && !atTop;
    }

    if (!shouldConsume) return;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") {
      e.stopImmediatePropagation();
    }

    scroller.scrollTop += delta;
  };
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
